import type { Actress, BwhCategory, DmmWork, FeedItem, FilterState } from "./types.ts";
import { DEFAULT_FILTER } from "./types.ts";

const API_BASE = "https://api.dekapai.dev/v1";

function mapActress(raw: any): Actress {
  return {
    id: raw.id,
    name: raw.name,
    age: raw.age ?? 0,
    birthday: raw.birthday ?? null,
    height: raw.height ?? 0,
    bust: raw.bust ?? 0,
    waist: raw.waist ?? 0,
    hip: raw.hip ?? 0,
    cupSize: raw.cup_size ?? "",
    tags: raw.tags ?? [],
    imageUrl: raw.image_url ?? "",
    createdAt: raw.created_at ?? "",
    debutAge: raw.debut_age ?? null,
    latestActiveAge: raw.latest_active_age ?? null,
  };
}

export async function fetchFeaturedActresses(
  page: number = 1
): Promise<{ data: Actress[]; total: number; totalPages: number }> {
  const res = await fetch(`${API_BASE}/actresses/featured?page=${page}`);
  const json: any = await res.json();
  return {
    data: json.data.map(mapActress),
    total: json.meta.total,
    totalPages: json.meta.total_pages,
  };
}

export async function fetchActresses(
  filter: FilterState,
  page: number = 1,
  perPage: number = 8,
  hasImage: boolean = true
): Promise<{ data: Actress[]; total: number; totalPages: number }> {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("per_page", String(perPage));
  params.set("sort", filter.sortBy);
  if (hasImage) params.set("has_image", "true");

  if (filter.search) params.set("q", filter.search);

  // テキスト検索時はカップ・年齢のデフォルトフィルターを適用しない
  // (名前検索で体型データがNULLの女優が除外されるのを防ぐ)
  const skipDefaultFilters = !!filter.search;

  if (filter.bustRange[0] > DEFAULT_FILTER.bustRange[0]) params.set("bust_min", String(filter.bustRange[0]));
  if (filter.bustRange[1] < DEFAULT_FILTER.bustRange[1]) params.set("bust_max", String(filter.bustRange[1]));
  if (filter.waistRange[0] > DEFAULT_FILTER.waistRange[0]) params.set("waist_min", String(filter.waistRange[0]));
  if (filter.waistRange[1] < DEFAULT_FILTER.waistRange[1]) params.set("waist_max", String(filter.waistRange[1]));
  if (filter.hipRange[0] > DEFAULT_FILTER.hipRange[0]) params.set("hip_min", String(filter.hipRange[0]));
  if (filter.hipRange[1] < DEFAULT_FILTER.hipRange[1]) params.set("hip_max", String(filter.hipRange[1]));
  if (filter.heightRange[0] > DEFAULT_FILTER.heightRange[0]) params.set("height_min", String(filter.heightRange[0]));
  if (filter.heightRange[1] < DEFAULT_FILTER.heightRange[1]) params.set("height_max", String(filter.heightRange[1]));
  if (!skipDefaultFilters && filter.cupSizes.length > 0) params.set("cup_sizes", filter.cupSizes.join(","));
  if (!skipDefaultFilters && filter.ageGroups.length > 0) {
    params.set("age_groups", filter.ageGroups.join(","));
    if (filter.ageType === "active") params.set("age_type", "active");
  }
  if (filter.tags.length > 0) params.set("tags", filter.tags.join(","));

  const res = await fetch(`${API_BASE}/actresses?${params}`);
  const json: any = await res.json();

  return {
    data: json.data.map(mapActress),
    total: json.meta.total,
    totalPages: json.meta.total_pages,
  };
}

export async function fetchActressByBwh(
  category: BwhCategory,
  min: number,
  max: number,
  page: number = 1,
  perPage: number = 8,
  hasImage: boolean = true,
  sort?: string,
  q?: string
): Promise<{ data: Actress[]; total: number; totalPages: number }> {
  const params = new URLSearchParams({
    category,
    min: String(min),
    max: String(max),
    page: String(page),
    per_page: String(perPage),
  });
  if (hasImage) params.set("has_image", "true");
  if (sort) params.set("sort", sort);
  if (q) params.set("q", q);

  const res = await fetch(`${API_BASE}/actresses/by-measurement?${params}`);
  const json: any = await res.json();

  return {
    data: json.data.map(mapActress),
    total: json.meta.total,
    totalPages: json.meta.total_pages,
  };
}

export async function fetchActressById(id: string): Promise<Actress | null> {
  const res = await fetch(`${API_BASE}/actresses/${id}`);
  if (!res.ok) return null;
  const json: any = await res.json();
  return mapActress(json.data);
}

export async function fetchSimilarActresses(actress: Actress, limit: number = 4): Promise<Actress[]> {
  const res = await fetch(`${API_BASE}/actresses/${actress.id}/similar?limit=${limit}`);
  const json: any = await res.json();
  return json.data.map(mapActress);
}

export async function fetchTags(): Promise<{ name: string; count: number }[]> {
  const res = await fetch(`${API_BASE}/tags`);
  const json: any = await res.json();
  return json.data;
}

export async function fetchActressWorks(
  actressId: string,
  page: number = 1,
  perPage: number = 20
): Promise<{ data: DmmWork[]; total: number }> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  const res = await fetch(`${API_BASE}/actresses/${actressId}/works?${params}`);
  if (!res.ok) return { data: [], total: 0 };
  const json: any = await res.json();
  return {
    data: json.data.map((w: any) => ({
      contentId: w.content_id,
      title: w.title,
      url: w.url,
      imageUrl: w.image_url ?? "",
      date: w.date ?? "",
      genres: w.genres ?? [],
      campaign: w.campaign ?? null,
      listPrice: w.list_price ?? null,
      minPrice: w.min_price ?? null,
    })),
    total: json.meta?.total ?? 0,
  };
}

export async function fetchFeed(
  limit: number = 20,
  genre?: string,
  onSale?: boolean
): Promise<{ data: FeedItem[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (genre) params.set("genre", genre);
  if (onSale) params.set("on_sale", "true");
  const res = await fetch(`${API_BASE}/feed?${params}`);
  const json: any = await res.json();
  return {
    data: json.data,
    total: json.meta?.total ?? 0,
  };
}

export async function fetchFeedGenres(): Promise<{ name: string; count: number }[]> {
  const res = await fetch(`${API_BASE}/feed/genres`);
  const json: any = await res.json();
  return json.data ?? [];
}

export interface SaleItem {
  id: number;
  contentId: string;
  title: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  affiliateUrl: string | null;
  date: string | null;
  minPrice: number | null;
  listPrice: number | null;
  genres: string[];
  actress: { id: string; name: string; imageUrl: string | null };
}

export interface SaleCampaign {
  title: string;
  start: string;
  end: string;
  items: SaleItem[];
}

export async function fetchSaleItems(opts?: {
  q?: string;
  genre?: string;
  actress?: string;
}): Promise<{ data: SaleCampaign[]; total: number }> {
  const params = new URLSearchParams();
  if (opts?.q) params.set("q", opts.q);
  if (opts?.genre) params.set("genre", opts.genre);
  if (opts?.actress) params.set("actress", opts.actress);
  const qs = params.toString();
  const res = await fetch(`${API_BASE}/feed/sale${qs ? `?${qs}` : ""}`);
  const json: any = await res.json();
  return {
    data: json.data ?? [],
    total: json.meta?.total ?? 0,
  };
}
